import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { uuid, notify } from '../../core/helpers';
import { Icon, InlineIcon } from '../../components/Icon';

// ─── REQUEST A CALL BACK ──────────────────────────────────────────────
// Client-facing. Files a high-priority "callback" ticket and notifies the
// assigned team + admins, then shows a reassuring confirmation window.
function RequestCallbackModal({ onClose }) {
  const { user, clients, employees, myCompanies, dbCreateTicket, showToast, runSubmitting } = useApp();
  const [reason, setReason] = useState("");
  const [preferred, setPreferred] = useState("");
  const [done, setDone] = useState(false);

  const company =
    (myCompanies && myCompanies[0]) ||
    (clients || []).find(c => c.email === user?.email || c.phone === user?.phone) ||
    null;

  const submit = async () => {
    if (!reason.trim()) { showToast("Please tell us what you'd like to discuss", "error"); return; }
    await runSubmitting('Sending call-back request…', async () => {
    const ticketNo = "CB/" + new Date().getFullYear() + "/" + Date.now().toString(36).slice(-4).toUpperCase();
    const ticketData = {
      id: uuid(),
      ticketNo,
      clientId: company?.id || null,
      clientName: company?.name || user?.name || "",
      subject: "Call-back request" + (preferred ? ` · Preferred time: ${preferred}` : ""),
      description: reason.trim(),
      priority: "high",
      status: "open",
      raisedBy: user?.id || null,
      assignedTo: company?.assignedTo || null,
    };
    try {
      await dbCreateTicket(ticketData);
      const teamIds = company?.assignedTo
        ? [company.assignedTo]
        : (employees || []).map(e => e.id).filter(Boolean);
      if (teamIds.length) {
        notify(teamIds, {
          type: "ticket",
          title: "Call-back requested",
          body: `${ticketData.clientName || "A client"} asked for a call back: ${reason.trim().slice(0, 90)}`,
          meta: { ticketId: ticketData.id, modal: "tickets" },
        });
      }
      setDone(true);
    } catch (e) {
      showToast("Could not submit right now — please try again.", "error");
    }
    }, 'ticket');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 460 }}>
      {!done ? (
        <>
          <div className="modal-head">
            <div className="modal-title"><InlineIcon name="phoneCall" size={18} gap={8}>Request a Call Back</InlineIcon></div>
            <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
          </div>
          <div className="modal-body">
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
              Tell us what you need and our team will call you back. This raises a priority ticket for {company?.name || "your account"}.
            </div>
            <div className="form-grid">
              <div className="f-group">
                <label className="f-label">What would you like to discuss? <span className="f-req">*</span></label>
                <textarea className="f-textarea" rows={4} placeholder="e.g. Questions about my GST filing / a new service / an invoice…"
                  value={reason} onChange={e => setReason(e.target.value)} />
              </div>
              <div className="f-group">
                <label className="f-label">Preferred time (optional)</label>
                <input className="f-input" placeholder="e.g. Tomorrow after 4 PM"
                  value={preferred} onChange={e => setPreferred(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>
              <InlineIcon name="send" size={14} gap={6}>Request Call Back</InlineIcon>
            </button>
          </div>
        </>
      ) : (
        <div style={{ padding: "44px 28px", textAlign: "center" }}>
          <div style={{ marginBottom: 16, color: "var(--green)" }}><Icon name="success" size={52} /></div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, marginBottom: 8 }}>Our team is on it!</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 22, lineHeight: 1.6 }}>
            Thanks{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — we've logged your request and notified the team.
            You'll get a call back shortly{preferred ? ` (${preferred})` : ""}.
          </div>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      )}
    </div>
  );
}

export { RequestCallbackModal };
